import { Injectable, signal, computed } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, tap, catchError, throwError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  LoginRequest,
  LoginResponse,
  LoginResponseData,
  LogoutResponse,
  RefreshTokenResponse,
  RefreshTokenData,
  UserProfileResponse,
  UpdateProfileRequest,
  ChangePasswordRequest,
  ChangePasswordResponse,
  AuthUser
} from '../models/auth.model';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private readonly baseUrl = environment.apiUrl || 'https://dev.api.putri.awh.co.id';
  private readonly TOKEN_KEY = 'putri_access_token';
  private readonly USER_KEY = 'putri_user';
  private readonly EXPIRES_AT_KEY = 'putri_token_expires_at';
  private readonly REMEMBER_KEY = 'putri_remember_me';

  // Reactive state using Angular Signals
  currentUser = signal<AuthUser | null>(this.getStoredUser());
  isAuthenticated = computed(() => !!this.currentUser() && !!this.getToken());

  constructor(private http: HttpClient) {
    // Verifikasi validitas sesi saat inisialisasi tanpa memblokir aplikasi
    if (this.isLoggedIn()) {
      // Periksa apakah waktu expired sudah lewat
      const expiresAt = localStorage.getItem(this.EXPIRES_AT_KEY);
      if (expiresAt) {
        const expTime = parseInt(expiresAt, 10);
        if (!isNaN(expTime) && Date.now() > expTime) {
          this.clearSession();
        }
      }
    }
  }

  /**
   * Mengirim kredensial email/username & password ke API Authentication
   * POST /api/v1/auth/login
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    // Bersihkan sesi atau token lama terlebih dahulu agar tidak mencemari request login
    this.clearSession();

    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    // Catatan backend: Parameter remember bernilai true memicu bug di server Tymon JWT
    // (setTTL null menyebabkan token langsung dianggap kedaluwarsa oleh validator backend).
    // Maka backend selalu kita beri false, sedangkan status remember me tetap disimpan di sisi klien.
    const payload: any = {
      password: credentials.password,
      remember: false
    };

    // Auto-resolve jika input dimasukkan via email / username
    const identifier = (credentials.email || credentials.username || '').trim();
    if (identifier) {
      if (identifier.includes('@')) {
        payload.email = identifier;
      } else {
        payload.username = identifier;
      }
    } else {
      if (credentials.email) payload.email = credentials.email;
      if (credentials.username) payload.username = credentials.username;
    }

    return this.http.post<LoginResponse>(
      `${this.baseUrl}/api/v1/auth/login`,
      payload,
      { headers }
    ).pipe(
      tap((res) => {
        if (res && res.success && res.data) {
          this.saveSession(res.data, credentials.remember ?? false);
        }
      })
    );
  }

  /**
   * Logout pengguna dan membatalkan token JWT di backend
   * POST /api/v1/auth/logout
   */
  logout(): Observable<LogoutResponse> {
    const headers = new HttpHeaders({
      'Accept': 'application/json'
    });

    return this.http.post<LogoutResponse>(
      `${this.baseUrl}/api/v1/auth/logout`,
      {},
      { headers }
    ).pipe(
      tap({
        next: () => this.clearSession(),
        error: () => this.clearSession()
      })
    );
  }

  /**
   * Memperpanjang masa aktif JWT token dengan men-generate access token baru
   * POST /api/v1/auth/refresh
   */
  refreshToken(): Observable<RefreshTokenResponse> {
    const headers = new HttpHeaders({
      'Accept': 'application/json'
    });

    return this.http.post<RefreshTokenResponse>(
      `${this.baseUrl}/api/v1/auth/refresh`,
      {},
      { headers }
    ).pipe(
      tap((res) => {
        if (res && res.success && res.data) {
          this.updateToken(res.data);
        }
      })
    );
  }

  /**
   * Mengambil data profil pengguna yang saat ini sedang login
   * GET /api/v1/auth/me
   */
  getProfile(): Observable<UserProfileResponse> {
    const headers = new HttpHeaders({
      'Accept': 'application/json'
    });

    return this.http.get<UserProfileResponse>(
      `${this.baseUrl}/api/v1/auth/me`,
      { headers }
    ).pipe(
      tap((res) => {
        if (res && res.success && res.data) {
          this.updateStoredUser(res.data);
        }
      }),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          this.clearSession();
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Memperbarui token yang diperpanjang dari refresh token
   */
  updateToken(data: RefreshTokenData): void {
    const token = data.access_token || data.token;
    if (!token) return;

    const isLocal = !!localStorage.getItem(this.TOKEN_KEY) || !!localStorage.getItem('wow_access_token');
    const storage = isLocal ? localStorage : sessionStorage;

    storage.setItem(this.TOKEN_KEY, token);

    if (data.expires_in) {
      const expiresAt = Date.now() + data.expires_in * 1000;
      storage.setItem(this.EXPIRES_AT_KEY, expiresAt.toString());
    }
  }

  /**
   * Memperbarui informasi profil pengguna yang sedang login
   * PUT /api/v1/account/my-profile (fallback bila endpoint update akun tersedia)
   */
  updateProfile(payload: UpdateProfileRequest): Observable<UserProfileResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.put<UserProfileResponse>(
      `${this.baseUrl}/api/v1/account/my-profile`,
      payload,
      { headers }
    ).pipe(
      tap((res) => {
        if (res && res.success && res.data) {
          this.updateStoredUser(res.data);
        }
      })
    );
  }

  /**
   * Mengubah password akun pengguna yang sedang login
   * PUT /api/v1/account/change-password (fallback bila endpoint change-password tersedia)
   */
  changePassword(payload: ChangePasswordRequest): Observable<ChangePasswordResponse> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    });

    return this.http.put<ChangePasswordResponse>(
      `${this.baseUrl}/api/v1/account/change-password`,
      payload,
      { headers }
    );
  }

  /**
   * Simpan token dan data user ke storage lokal
   */
  saveSession(data: LoginResponseData, remember: boolean = false): void {
    const storage = remember ? localStorage : sessionStorage;

    // Bersihkan storage sebelum menulis baru
    this.clearStorageData();

    const token = data.access_token || data.token;
    if (token) {
      storage.setItem(this.TOKEN_KEY, token);
    }

    if (data.user) {
      storage.setItem(this.USER_KEY, JSON.stringify(data.user));
      this.currentUser.set(data.user);
    }

    if (data.expires_in) {
      const expiresAt = Date.now() + data.expires_in * 1000;
      storage.setItem(this.EXPIRES_AT_KEY, expiresAt.toString());
    }

    if (remember) {
      localStorage.setItem(this.REMEMBER_KEY, 'true');
    } else {
      localStorage.removeItem(this.REMEMBER_KEY);
    }
  }

  /**
   * Dapatkan JWT token aktif
   */
  getToken(): string | null {
    return (
      localStorage.getItem(this.TOKEN_KEY) ||
      sessionStorage.getItem(this.TOKEN_KEY) ||
      localStorage.getItem('wow_access_token') ||
      sessionStorage.getItem('wow_access_token')
    );
  }

  /**
   * Cek apakah user saat ini memiliki sesi login aktif yang sah
   */
  isLoggedIn(): boolean {
    const token = this.getToken();
    if (!token) return false;

    const expiresAt =
      localStorage.getItem(this.EXPIRES_AT_KEY) ||
      sessionStorage.getItem(this.EXPIRES_AT_KEY) ||
      localStorage.getItem('wow_token_expires_at') ||
      sessionStorage.getItem('wow_token_expires_at');

    if (expiresAt && Date.now() > Number(expiresAt)) {
      this.clearSession();
      return false;
    }
    return true;
  }

  /**
   * Dapatkan user yang sedang tersimpan di storage (null jika belum login)
   */
  getStoredUser(): AuthUser | null {
    try {
      const raw =
        localStorage.getItem(this.USER_KEY) ||
        sessionStorage.getItem(this.USER_KEY) ||
        localStorage.getItem('wow_user') ||
        sessionStorage.getItem('wow_user');
      if (raw) {
        return JSON.parse(raw);
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Update data profil user di storage dan signal
   */
  updateStoredUser(user: AuthUser): void {
    const isLocal = !!localStorage.getItem(this.TOKEN_KEY) || !!localStorage.getItem('wow_access_token');
    const storage = isLocal ? localStorage : sessionStorage;
    storage.setItem(this.USER_KEY, JSON.stringify(user));
    this.currentUser.set(user);
  }

  /**
   * Cek apakah user yang login memiliki permission tertentu
   */
  hasPermission(permission: string): boolean {
    const user = this.currentUser();
    if (!user || !user.roles) return false;
    for (const r of user.roles) {
      if (typeof r === 'object' && Array.isArray((r as any).permissions)) {
        if ((r as any).permissions.includes(permission)) return true;
      }
    }
    return false;
  }

  /**
   * Cek apakah user memiliki salah satu dari daftar permissions
   */
  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some((p) => this.hasPermission(p));
  }

  /**
   * Ambil seluruh daftar permission unik milik user
   */
  getUserPermissions(): string[] {
    const user = this.currentUser();
    if (!user || !user.roles) return [];
    const set = new Set<string>();
    for (const r of user.roles) {
      if (typeof r === 'object' && Array.isArray((r as any).permissions)) {
        (r as any).permissions.forEach((p: string) => set.add(p));
      }
    }
    return Array.from(set);
  }

  /**
   * Hapus seluruh sesi login dan token
   */
  clearSession(): void {
    this.clearStorageData();
    this.currentUser.set(null);
  }

  private clearStorageData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.EXPIRES_AT_KEY);
    localStorage.removeItem('wow_access_token');
    localStorage.removeItem('wow_user');
    localStorage.removeItem('wow_token_expires_at');

    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.USER_KEY);
    sessionStorage.removeItem(this.EXPIRES_AT_KEY);
    sessionStorage.removeItem('wow_access_token');
    sessionStorage.removeItem('wow_user');
    sessionStorage.removeItem('wow_token_expires_at');
  }
}
